// shop-frontend/src/pages/ShopBulkUpload.jsx
import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  FileUp,
} from "lucide-react";
import {
  getBulkUploadTemplateUrl,
  validateBulkUploadCSV,
  uploadBulkProducts,
  getShopToken,
} from "../utils/api";

/* =========================
   Status Badge
   ========================= */
function StatusBadge({ type, children }) {
  const styles = {
    success: "bg-emerald-500/20 text-emerald-400",
    error: "bg-red-500/20 text-red-400",
    warning: "bg-yellow-500/20 text-yellow-400",
    info: "bg-blue-500/20 text-blue-400",
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${styles[type] || styles.info}`}>
      {children}
    </span>
  );
}

/* =========================
   Main Bulk Upload Page
   ========================= */
export default function ShopBulkUpload() {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile && droppedFile.name.endsWith(".csv")) {
      setFile(droppedFile);
      setValidation(null);
      setUploadResult(null);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidation(null);
      setUploadResult(null);
    }
  };

  const handleValidate = async () => {
    if (!file) return;
    setValidating(true);
    setValidation(null);

    try {
      const result = await validateBulkUploadCSV(file);
      setValidation(result);
    } catch (err) {
      setValidation({ ok: false, error: err.message });
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);

    try {
      const result = await uploadBulkProducts(file);
      setUploadResult(result);
      if (result.created > 0) {
        setFile(null);
        setValidation(null);
      }
    } catch (err) {
      setUploadResult({ ok: false, error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const token = getShopToken();
    const url = getBulkUploadTemplateUrl();
    
    // Create a link and trigger download
    const link = document.createElement("a");
    link.href = url + `?token=${token}`;
    link.download = "product-upload-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-zinc-950 page-responsive">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/products"
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">Bulk Product Upload</h1>
            <p className="text-zinc-400 text-sm">Upload multiple products at once via CSV</p>
          </div>
        </div>

        {/* Template Download */}
        <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-medium mb-1">Step 1: Download Template</h3>
              <p className="text-zinc-400 text-sm mb-3">
                Start with our CSV template that includes all required columns and example data.
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
            </div>
          </div>
        </div>

        {/* Required Fields Info */}
        <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 mb-6">
          <h3 className="text-white font-medium mb-3">Required CSV Columns</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-zinc-300">title</span>
              <StatusBadge type="error">Required</StatusBadge>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-zinc-300">price</span>
              <StatusBadge type="error">Required</StatusBadge>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-zinc-300">category</span>
              <StatusBadge type="error">Required</StatusBadge>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <span className="text-sm">description</span>
              <StatusBadge type="info">Optional</StatusBadge>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <span className="text-sm">quantity</span>
              <StatusBadge type="info">Optional</StatusBadge>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <span className="text-sm">images</span>
              <StatusBadge type="info">Optional</StatusBadge>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-3">
            For images, provide comma-separated URLs. Tags can also be comma-separated.
          </p>
        </div>

        {/* File Upload */}
        <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 mb-6">
          <h3 className="text-white font-medium mb-4">Step 2: Upload Your CSV</h3>
          
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive
                ? "border-emerald-500 bg-emerald-500/10"
                : file
                ? "border-zinc-600 bg-zinc-800/50"
                : "border-zinc-700 hover:border-zinc-600"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            {file ? (
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 bg-emerald-500/10 rounded-full">
                  <FileUp className="w-8 h-8 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-zinc-400 text-sm">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setFile(null);
                    setValidation(null);
                    setUploadResult(null);
                  }}
                  className="text-sm text-red-400 hover:text-red-300"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 bg-zinc-800 rounded-full">
                  <Upload className="w-8 h-8 text-zinc-400" />
                </div>
                <div>
                  <p className="text-white">
                    Drag & drop your CSV file here
                  </p>
                  <p className="text-zinc-400 text-sm">or click to browse</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {file && (
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleValidate}
                disabled={validating || uploading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {validating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Validate First
                  </>
                )}
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || validating}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Products
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Validation Results */}
        {validation && (
          <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 mb-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              {validation.ok ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Validation Results
                </>
              ) : validation.error ? (
                <>
                  <XCircle className="w-5 h-5 text-red-400" />
                  Validation Failed
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  Validation Results
                </>
              )}
            </h3>

            {validation.error ? (
              <p className="text-red-400">{validation.error}</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-zinc-800 rounded-lg">
                    <div className="text-2xl font-bold text-white">{validation.totalRows}</div>
                    <div className="text-xs text-zinc-400">Total Rows</div>
                  </div>
                  <div className="text-center p-3 bg-emerald-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-400">{validation.validRows}</div>
                    <div className="text-xs text-zinc-400">Valid</div>
                  </div>
                  <div className="text-center p-3 bg-red-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-red-400">{validation.invalidRows}</div>
                    <div className="text-xs text-zinc-400">Invalid</div>
                  </div>
                </div>

                {/* Preview */}
                {validation.preview?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm text-zinc-400 mb-2">Preview (first 5 valid rows)</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-zinc-400 border-b border-zinc-800">
                            <th className="pb-2">Row</th>
                            <th className="pb-2">Title</th>
                            <th className="pb-2">Price</th>
                            <th className="pb-2">Category</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validation.preview.map((item, i) => (
                            <tr key={i} className="border-b border-zinc-800">
                              <td className="py-2 text-zinc-500">{item.row}</td>
                              <td className="py-2 text-white">{item.title}</td>
                              <td className="py-2 text-emerald-400">${item.price}</td>
                              <td className="py-2 text-zinc-300">{item.category}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {validation.errors?.length > 0 && (
                  <div>
                    <h4 className="text-sm text-red-400 mb-2">
                      Errors ({validation.errors.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {validation.errors.slice(0, 10).map((err, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-2 text-sm p-2 bg-red-500/10 rounded"
                        >
                          <span className="text-red-400 font-medium">Row {err.row}:</span>
                          <span className="text-zinc-300">{err.errors.join(", ")}</span>
                        </div>
                      ))}
                      {validation.errors.length > 10 && (
                        <p className="text-zinc-500 text-sm">
                          ...and {validation.errors.length - 10} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Upload Results */}
        {uploadResult && (
          <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              {uploadResult.ok ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Upload Complete
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-400" />
                  Upload Failed
                </>
              )}
            </h3>

            {uploadResult.error ? (
              <p className="text-red-400">{uploadResult.error}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-4 bg-emerald-500/10 rounded-lg">
                    <div className="text-3xl font-bold text-emerald-400">{uploadResult.created}</div>
                    <div className="text-sm text-zinc-400">Products Created</div>
                  </div>
                  <div className="text-center p-4 bg-red-500/10 rounded-lg">
                    <div className="text-3xl font-bold text-red-400">{uploadResult.failed}</div>
                    <div className="text-sm text-zinc-400">Failed</div>
                  </div>
                </div>

                {uploadResult.created > 0 && (
                  <Link
                    to="/products"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                  >
                    View My Products
                  </Link>
                )}

                {uploadResult.errors?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm text-red-400 mb-2">Failed rows</h4>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {uploadResult.errors.map((err, i) => (
                        <div
                          key={i}
                          className="text-sm p-2 bg-red-500/10 rounded"
                        >
                          <span className="text-red-400">Row {err.row}</span>
                          <span className="text-zinc-400"> ({err.title}): </span>
                          <span className="text-zinc-300">{err.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
